/**
 * Settings card "Connected MCP servers": add, edit, test and remove a person's own remote MCP
 * servers, see the tools each offers, and choose per tool whether Flux Agent asks first or may
 * call it without asking. Every rule (URL checks, limits, encryption, the confirm-by-default
 * policy) is enforced by the Worker; this card only shows state and sends edits.
 *
 * @module apps/web/src/components/settings/McpServersCard
 */
import { useEffect, useState } from 'react';
import { Loader2, Plug, Plus, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  fluxAgentMcpApi,
  type McpAuthKind,
  type McpServer,
  type McpToolPolicy,
} from '@/lib/fluxAgentMcpApi';
import { cn } from '@/lib/utils';
import {
  emptyForm,
  formFromServer,
  formToInput,
  serverStatus,
  type McpServerFormState,
} from './mcpServerForm';

/** Most servers a person may add; the Worker enforces the same limit. */
const MAX_SERVERS = 10;

/** Error text of an unknown failure. */
function messageOf(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

/** List, add, edit and test the caller's MCP servers and set each tool's policy. */
export function McpServersCard() {
  const [servers, setServers] = useState<McpServer[] | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    fluxAgentMcpApi.list().then(
      value => current && setServers(value),
      cause => current && setError(messageOf(cause, 'Could not load MCP servers.'))
    );
    return () => {
      current = false;
    };
  }, []);

  /** Replace one server in the list with the server's answer. */
  const replace = (server: McpServer) =>
    setServers(list => {
      const items = list ?? [];
      return items.some(item => item.id === server.id)
        ? items.map(item => (item.id === server.id ? server : item))
        : [...items, server];
    });

  /** Run one request with a busy marker and error capture. */
  const run = async (key: string, action: () => Promise<void>, fallback: string) => {
    setBusy(key);
    setError('');
    try {
      await action();
    } catch (cause) {
      setError(messageOf(cause, fallback));
    } finally {
      setBusy(null);
    }
  };

  /** Save the add or edit form. */
  const save = (form: McpServerFormState, existing?: McpServer) =>
    run(
      existing ? `save-${existing.id}` : 'create',
      async () => {
        const input = formToInput(form, existing);
        replace(
          existing
            ? await fluxAgentMcpApi.update(existing.id, input)
            : await fluxAgentMcpApi.create(input)
        );
        setEditing(null);
      },
      'Could not save the server.'
    );

  /** Remove a server after the person confirms. */
  const remove = (server: McpServer) => {
    if (!window.confirm(`Remove “${server.name}”? Flux Agent will no longer see its tools.`))
      return;
    void run(
      `remove-${server.id}`,
      async () => {
        await fluxAgentMcpApi.remove(server.id);
        setServers(list => (list ?? []).filter(item => item.id !== server.id));
      },
      'Could not remove the server.'
    );
  };

  const count = servers?.length ?? 0;
  return (
    <Card data-testid="mcp-servers">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plug className="h-5 w-5" aria-hidden="true" />
          Connected MCP servers
        </CardTitle>
        <CardDescription>
          Let Flux Agent use tools from your own MCP servers. Every call shows you a Confirm /
          Cancel card first unless you allow that tool. A server sees what Flux Agent sends it, so
          only connect servers you trust.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {servers === null && !error && (
          <p className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading servers…
          </p>
        )}
        {servers?.length === 0 && editing !== 'new' && (
          <p className="text-muted-foreground">No servers yet.</p>
        )}
        {servers?.map(server =>
          editing === server.id ? (
            <ServerForm
              key={server.id}
              initial={formFromServer(server)}
              existing={server}
              saving={busy === `save-${server.id}`}
              onCancel={() => setEditing(null)}
              onSave={form => save(form, server)}
            />
          ) : (
            <ServerRow
              key={server.id}
              server={server}
              busy={busy}
              onEdit={() => setEditing(server.id)}
              onRemove={() => remove(server)}
              onTest={() =>
                run(
                  `test-${server.id}`,
                  async () => replace(await fluxAgentMcpApi.discover(server.id)),
                  'Could not reach the server.'
                )
              }
              onToggle={enabled =>
                run(
                  `toggle-${server.id}`,
                  async () => replace(await fluxAgentMcpApi.update(server.id, { enabled })),
                  'Could not save the server.'
                )
              }
              onPolicy={(toolId, policy) =>
                run(
                  `policy-${toolId}`,
                  async () =>
                    replace(await fluxAgentMcpApi.setToolPolicy(server.id, toolId, policy)),
                  'Could not change the tool.'
                )
              }
            />
          )
        )}
        {editing === 'new' ? (
          <ServerForm
            initial={emptyForm()}
            saving={busy === 'create'}
            onCancel={() => setEditing(null)}
            onSave={form => save(form)}
          />
        ) : (
          servers !== null && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={count >= MAX_SERVERS || editing !== null}
              onClick={() => setEditing('new')}
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              Add server
            </Button>
          )
        )}
        {count >= MAX_SERVERS && (
          <p className="text-muted-foreground">You can connect at most {MAX_SERVERS} servers.</p>
        )}
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** Inputs for {@link ServerRow}. */
interface ServerRowProps {
  /** The server shown. */
  server: McpServer;
  /** Key of the request in flight, if any; disables this row's buttons while it is the row's own. */
  busy: string | null;
  /** Open the edit form. */
  onEdit: () => void;
  /** Ask to remove the server. */
  onRemove: () => void;
  /** Test the connection and refresh tools. */
  onTest: () => void;
  /** Turn the server's tools on or off for Flux Agent. */
  onToggle: (enabled: boolean) => void;
  /** Change one tool's policy. */
  onPolicy: (toolId: string, policy: McpToolPolicy) => void;
}

/** One server with its status, actions and tools.
 * @component ServerRow
 */
function ServerRow({ server, busy, onEdit, onRemove, onTest, onToggle, onPolicy }: ServerRowProps) {
  const status = serverStatus(server);
  const testing = busy === `test-${server.id}`;
  return (
    <section aria-label={server.name} className="space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="flex flex-wrap items-center gap-2 font-medium">
            {server.name}
            {!server.enabled && <Badge variant="secondary">Off</Badge>}
            {server.hasCredential && <Badge variant="outline">Credential saved</Badge>}
          </p>
          <p className="text-muted-foreground break-all">{server.url}</p>
          <p
            className={cn(status.tone === 'error' ? 'text-destructive' : 'text-muted-foreground')}
            data-testid="mcp-server-status"
          >
            {status.text}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" disabled={testing} onClick={onTest}>
            {testing ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="mr-1 h-4 w-4" aria-hidden="true" />
            )}
            Test connection
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onEdit}
            aria-label={`Edit ${server.name}`}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onRemove}
            disabled={busy === `remove-${server.id}`}
            aria-label={`Remove ${server.name}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
      <label className="flex items-center gap-2">
        <Checkbox
          checked={server.enabled}
          disabled={busy === `toggle-${server.id}`}
          onCheckedChange={checked => onToggle(Boolean(checked))}
        />
        <span>Let Flux Agent use this server</span>
      </label>
      {server.tools.length > 0 && (
        <ul className="divide-y rounded-md border" aria-label={`Tools on ${server.name}`}>
          {server.tools.map(tool => (
            <li key={tool.id} className="flex flex-wrap items-start justify-between gap-2 p-2">
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="font-medium">
                  {tool.title || tool.name}
                  {tool.annotations.readOnlyHint && (
                    <span className="text-muted-foreground ml-2 text-xs font-normal">
                      server says read-only
                    </span>
                  )}
                </p>
                {tool.description && (
                  <p className="text-muted-foreground line-clamp-2">{tool.description}</p>
                )}
              </div>
              <select
                aria-label={`When Flux Agent calls ${tool.title || tool.name}`}
                className="bg-background h-8 rounded-md border px-2 text-sm"
                value={tool.policy}
                disabled={busy === `policy-${tool.id}`}
                onChange={event => onPolicy(tool.id, event.target.value as McpToolPolicy)}
              >
                <option value="ask">Ask first</option>
                <option value="allow">Allowed without asking</option>
              </select>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Inputs for {@link ServerForm}. */
interface ServerFormProps {
  /** Starting values. */
  initial: McpServerFormState;
  /** The server being edited; absent when adding. */
  existing?: McpServer;
  /** Whether the save request is in flight. */
  saving: boolean;
  /** Close the form without saving. */
  onCancel: () => void;
  /** Save the values. */
  onSave: (form: McpServerFormState) => void;
}

/** Add or edit form for one server. The secret field starts empty and is never shown back.
 * @component ServerForm
 */
function ServerForm({ initial, existing, saving, onCancel, onSave }: ServerFormProps) {
  const [form, setForm] = useState(initial);
  const prefix = existing ? `mcp-${existing.id}` : 'mcp-new';
  const set = (patch: Partial<McpServerFormState>) => setForm(value => ({ ...value, ...patch }));
  const keepHint =
    existing?.hasCredential && form.authKind === existing.authKind
      ? 'Leave empty to keep the saved one.'
      : '';
  return (
    <form
      aria-label={existing ? `Edit ${existing.name}` : 'Add MCP server'}
      className="space-y-3 rounded-md border p-3"
      onSubmit={event => {
        event.preventDefault();
        onSave(form);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={`${prefix}-name`}>Name</Label>
          <Input
            id={`${prefix}-name`}
            value={form.name}
            maxLength={40}
            required
            onChange={event => set({ name: event.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${prefix}-url`}>Server address</Label>
          <Input
            id={`${prefix}-url`}
            type="url"
            inputMode="url"
            placeholder="https://mcp.example.com/mcp"
            value={form.url}
            maxLength={512}
            required
            onChange={event => set({ url: event.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${prefix}-auth`}>Credential</Label>
          <select
            id={`${prefix}-auth`}
            className="bg-background h-10 w-full rounded-md border px-3 text-sm"
            value={form.authKind}
            onChange={event => set({ authKind: event.target.value as McpAuthKind })}
          >
            <option value="none">None</option>
            <option value="bearer">Bearer token</option>
            <option value="header">Custom header</option>
          </select>
        </div>
        {form.authKind === 'header' && (
          <div className="space-y-1">
            <Label htmlFor={`${prefix}-header`}>Header name</Label>
            <Input
              id={`${prefix}-header`}
              placeholder="X-Api-Key"
              value={form.headerName}
              maxLength={64}
              required
              onChange={event => set({ headerName: event.target.value })}
            />
          </div>
        )}
        {form.authKind !== 'none' && (
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={`${prefix}-secret`}>
              {form.authKind === 'bearer' ? 'Token' : 'Header value'}
            </Label>
            <Input
              id={`${prefix}-secret`}
              type="password"
              autoComplete="off"
              value={form.secret}
              maxLength={4096}
              required={!keepHint}
              placeholder={keepHint}
              onChange={event => set({ secret: event.target.value })}
            />
            <p className="text-muted-foreground text-xs">Stored encrypted and never shown again.</p>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={saving}>
          {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />}
          {existing ? 'Save' : 'Add and test'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
